import { useTranslation } from "@refinedev/core";
import Button from "components/button";
import { Calendar, Location } from "components/customIcons";
import { DropdownMenu, IMenuItem } from "components/dropdownMenu";
import { EventStepStatus, EventType, ISportEventItem } from "interfaces";
import { get } from "lodash";
import { Link } from "react-router-dom";
import { CREATE_EVENT_STEPS, DEFAULT_BANNER_URL } from "utills/constants";
import { getEventDate } from "utills/helpers";

interface EventCardProps {
  data?: ISportEventItem[];
  pageSize: number;
  currentPage: number;
  eventType: EventType;
  primaryButton?: (id: string, status: string) => {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  moreActions: (id: string) => IMenuItem[];
}

const getStatusStyle = (status: EventStepStatus) => {
  switch (status) {
    case EventStepStatus.DRAFT:
      return "bg-brand-secondary-400 text-brand-secondary-500";
    case EventStepStatus.PUBLISHED:
      return "bg-[#00A67E] text-white";
    case EventStepStatus.UNPUBLISHED:
      return "bg-[#F5F6F7] text-[#A8B3BD]";
    default:
      return "bg-white text-black";
  }
};

export const EventCard = ({
  data = [],
  primaryButton,
  moreActions,
}: EventCardProps) => {
  const { translate, getLocale } = useTranslation();
  const getStatusText = (status: EventStepStatus) => {
  switch (status) {
    case EventStepStatus.DRAFT:
      return translate("draft");
    case EventStepStatus.PUBLISHED:
      return translate("published");
    case EventStepStatus.UNPUBLISHED:
      return translate("unpublished");
    default:
      return "";
  }
};

  return (
    <div className="bg-white grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {data.map((item: ISportEventItem, index: number) => (
        <div
          className="flex flex-col shadow-md rounded-xl border-solid border-[#C5CCD2] border-[1px] p-4"
          key={item.id}
        >
          <div className="relative w-full overflow-hidden">
            <img
              src={item.banner_url ?? DEFAULT_BANNER_URL}
              alt={`${item.name} Banner`}
              className="rounded-lg"
            />
            <div
              className={`absolute top-2.5 right-2.5 px-2.5 py-1 rounded-lg ${getStatusStyle(
                item.status
              )}`}
            >
              <p className="text-xs sm:text-sm font-medium capitalize">
                {getStatusText(item.status)}
              </p>
            </div>
          </div>
          <div className="text-xs 2xl:text-sm flex flex-col justify-center items-start mt-4">
            <p className="text-lg font-bold line-clamp-2">
              {get(item, `name.${getLocale() ?? "en"}`, "")}
            </p>
            <div className="w-full flex flex-row items-start gap-x-2 mt-3">
              <div className="w-fit shrink-0 self-center">
                <Calendar />
              </div>
              <p className="capitalize">{getEventDate(item.start_date, item.end_date, getLocale())}</p>
            </div>
            <div className="w-full flex flex-row items-start gap-x-2 mt-2">
              <div className="w-fit shrink-0 mt-0.5">
                <Location />
              </div>
              <p>{get(item, `location.${getLocale() ?? "en"}`, "")}</p>
            </div>
          </div>
          <div className="mt-auto w-full flex flex-row-reverse flex-wrap justify-between items-center gap-2 pt-4">
            <div className="shrink-0 flex flex-row items-center gap-x-2.5">
              {moreActions ? (
                <DropdownMenu
                  position="top"
                  label={
                    <div className="text-xs sm:text-sm w-fit bg-white rounded-md px-3 text-brand-500 font-bold py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85">
                      {translate("more-actions")}
                    </div>
                  }
                  items={moreActions(item.id)}
                />
              ) : (
                <></>
              )}
              {primaryButton ? (
                <>
                  {primaryButton(item.id, item.status).href ? (
                    <Link to={primaryButton(item.id, item.status).href ?? "#"} target="_blank">
                      <Button className="text-xs sm:text-sm w-fit min-w-16 max-w-full border-solid border-brand-500 border-[1px] py-2">
                        {primaryButton(item.id, item.status).label}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      className="text-xs sm:text-sm w-fit min-w-16 max-w-full border-solid border-brand-500 border-[1px] py-2"
                      onClick={primaryButton(item.id, item.status).onClick}
                    >
                      {primaryButton(item.id, item.status).label}
                    </Button>
                  )}
                </>
              ) : <></>}
            </div>
            {item.status === EventStepStatus.DRAFT ? (
              <p className="text-xs sm:text-sm">
                {get(item, "current_step", 0)}/{CREATE_EVENT_STEPS.length} {translate("steps")}
              </p>
            ) : (
              <></>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
